import React, { useMemo, useState } from 'react'
import { Button } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'

import Icon from '../icon'
import SettingsCard from './SettingsCard'
import SelectionModal from './SelectionModal'
import styles from './SelectionModal.module.scss'

/**
 * Settings card fronting a SelectionModal: shows the current selection as
 * removable rows plus a button that opens the picker.
 *
 * The card owns its own persistence — both the modal's Save and a row's Remove
 * call `onSave`, which returns false on failure so the modal can stay open.
 * There is no page-level save button to fall back on.
 */
export default function SelectionCard({
  icon,
  title,
  description,
  options,
  value = [],
  onSave,
  max = 3,
  showIcons = false,
  buttonLabel,
  id,
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [removingId, setRemovingId] = useState(null)

  const selected = useMemo(
    () => value.map(v => options.find(o => o.id === v)).filter(Boolean),
    [value, options],
  )

  const handleRemove = async optionId => {
    setRemovingId(optionId)
    try {
      await onSave(value.filter(v => v !== optionId))
    } finally {
      setRemovingId(null)
    }
  }

  return (
    <SettingsCard icon={icon} title={title} description={description} id={id}>
      {selected.length > 0 ? (
        <div className={styles.selectedList}>
          {selected.map(option => (
            <div key={option.id} className={styles.selectedRow}>
              <span className={styles.selectedName}>
                {showIcons && option.icon && (
                  <Icon name={option.icon} className={styles.optionIcon} />
                )}
                {option.label}
              </span>
              <button
                type="button"
                className={styles.remove}
                onClick={() => handleRemove(option.id)}
                disabled={removingId != null}
              >
                {removingId === option.id
                  ? t('users.edit.saving')
                  : t('settings.selection.remove', 'Remove')}
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className={styles.empty}>
          {t('settings.selection.none', 'Nothing selected yet.')}
        </p>
      )}

      <Button variant="primary" onClick={() => setOpen(true)}>
        {buttonLabel}
      </Button>

      <SelectionModal
        show={open}
        onHide={() => setOpen(false)}
        onConfirm={onSave}
        title={title}
        description={description}
        options={options}
        value={value}
        max={max}
        showIcons={showIcons}
      />
    </SettingsCard>
  )
}

SelectionCard.propTypes = {
  icon: PropTypes.string,
  title: PropTypes.node,
  description: PropTypes.node,
  options: PropTypes.array.isRequired,
  value: PropTypes.array,
  /** Persists the new id list. Return false to signal failure. */
  onSave: PropTypes.func.isRequired,
  max: PropTypes.number,
  showIcons: PropTypes.bool,
  buttonLabel: PropTypes.node,
  id: PropTypes.string,
}
